import { useState, useRef, useEffect } from 'react'
import { Info, Bot, Database, TrendingUp, Zap } from 'lucide-react'

export default function InfoTooltip({ 
  title, 
  description, 
  aiMethod, 
  dataSource, 
  updateFrequency,
  icon = Info,
  iconColor = "text-blue-500",
  position = "auto" // Cambio por defecto a "auto"
}) {
  const [isVisible, setIsVisible] = useState(false)
  const [actualPosition, setActualPosition] = useState('top')
  const buttonRef = useRef(null)
  const tooltipRef = useRef(null)
  
  const IconComponent = icon

  // Función para calcular la mejor posición del tooltip
  const calculatePosition = () => {
    if (!buttonRef.current || position !== 'auto') {
      setActualPosition(position)
      return
    }

    const buttonRect = buttonRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth
    
    // Dimensiones aproximadas del tooltip
    const tooltipHeight = 200 // Estimación conservadora
    const tooltipWidth = 320 // 80 * 4 = 320px (w-80)
    
    // Espacio disponible en cada dirección
    const spaceTop = buttonRect.top
    const spaceBottom = viewportHeight - buttonRect.bottom
    const spaceLeft = buttonRect.left
    const spaceRight = viewportWidth - buttonRect.right
    
    // Decidir posición basada en espacio disponible
    let bestPosition = 'top'
    
    // Preferir arriba si hay suficiente espacio
    if (spaceTop >= tooltipHeight + 20) {
      bestPosition = 'top'
    }
    // Si no hay espacio arriba, usar abajo
    else if (spaceBottom >= tooltipHeight + 20) {
      bestPosition = 'bottom'
    }
    // Si no hay espacio vertical suficiente, usar lateral
    else if (spaceRight >= tooltipWidth + 20) {
      bestPosition = 'right'
    }
    else if (spaceLeft >= tooltipWidth + 20) {
      bestPosition = 'left'
    }
    // Como último recurso, usar bottom aunque se corte un poco
    else {
      bestPosition = 'bottom'
    }
    
    setActualPosition(bestPosition)
  }

  // Calcular posición cuando el tooltip se hace visible
  useEffect(() => {
    if (isVisible && position === 'auto') {
      calculatePosition()
    }
  }, [isVisible, position])

  const getPositionClasses = () => {
    switch (actualPosition) {
      case 'top':
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2'
      case 'bottom':
        return 'top-full left-1/2 transform -translate-x-1/2 mt-2'
      case 'left':
        return 'right-full top-1/2 transform -translate-y-1/2 mr-2'
      case 'right':
        return 'left-full top-1/2 transform -translate-y-1/2 ml-2'
      default:
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2'
    }
  }

  const getArrowClasses = () => {
    switch (actualPosition) {
      case 'top':
        return 'top-full left-1/2 -translate-x-1/2 -mt-1.5 border-b border-r'
      case 'bottom':
        return 'bottom-full left-1/2 -translate-x-1/2 -mb-1.5 border-t border-l'
      case 'left':
        return 'left-full top-1/2 -translate-y-1/2 -ml-1.5 border-t border-r'
      case 'right':
        return 'right-full top-1/2 -translate-y-1/2 -mr-1.5 border-b border-l'
      default:
        return 'top-full left-1/2 -translate-x-1/2 -mt-1.5 border-b border-r'
    }
  }

  const handleMouseEnter = () => {
    setIsVisible(true)
  }

  const handleMouseLeave = () => {
    setIsVisible(false)
  }

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`${iconColor} hover:scale-110 transition-transform duration-200 p-1 rounded-full hover:bg-gray-100`}
      >
        <IconComponent className="w-4 h-4" />
      </button>
      
      {isVisible && (
        <div 
          ref={tooltipRef}
          className={`absolute z-50 ${getPositionClasses()} w-80`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-left">
            {/* Flecha del tooltip */}
            <div className={`absolute w-3 h-3 bg-white border-gray-200 transform rotate-45 ${getArrowClasses()}`} />
            
            <div className="relative">
              <h4 className="font-semibold text-gray-900 mb-2 text-sm">{title}</h4>
              <p className="text-gray-700 text-xs leading-relaxed mb-3">{description}</p>
              
              <div className="space-y-2">
                {aiMethod && (
                  <div className="flex items-start space-x-2">
                    <Bot className="w-3 h-3 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-purple-800">Método IA:</p>
                      <p className="text-xs text-gray-600">{aiMethod}</p>
                    </div>
                  </div>
                )}
                
                {dataSource && (
                  <div className="flex items-start space-x-2">
                    <Database className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-blue-800">Fuente de datos:</p>
                      <p className="text-xs text-gray-600">{dataSource}</p>
                    </div>
                  </div>
                )}
                
                {updateFrequency && (
                  <div className="flex items-start space-x-2">
                    <TrendingUp className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-green-800">Actualización:</p>
                      <p className="text-xs text-gray-600">{updateFrequency}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 